/**
 * Apps2Go Hub – Ravintolan rooli-pohjaiset näkymät (Phase 18)
 *
 * Kolme erillistä alasivu-näkymää eri henkilökunnan rooleille:
 *  - /restaurant/kitchen   → Kokki   (jonotetut tilaukset + status-muutos)
 *  - /restaurant/cashier   → Kassa   (tarjoiltavat tilaukset + maksu)
 *  - /restaurant/waiter    → Tarjoilija (pöytäkaavio + tilaus pöydältä)
 *
 * Kaikki näkymät:
 *  - käyttävät olemassa olevia /api/restaurant -endpointteja
 *  - auto-refresh 5–8 s välein
 *  - mobile-first (tabletit ja puhelimet salissa)
 */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ChefHat, Receipt, Users, ArrowRight, CheckCircle2, Clock, Sparkles,
  Banknote, CreditCard, Smartphone, FileText, Loader2, Plus, Minus,
  Send, ShoppingBag, MapPin, X, Utensils,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const formatEur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;

const STATUS_LABELS = {
  received:   { label: "Vastaanotettu",         color: "text-blue-300",     bg: "bg-blue-500/15",    border: "border-blue-500/30" },
  preparing:  { label: "Valmistuksessa",        color: "text-amber-300",    bg: "bg-amber-500/15",   border: "border-amber-500/30" },
  ready:      { label: "Valmis tarjoiltavaksi", color: "text-emerald-300",  bg: "bg-emerald-500/15", border: "border-emerald-500/30" },
  served:     { label: "Tarjoiltu",             color: "text-purple-300",   bg: "bg-purple-500/15",  border: "border-purple-500/30" },
  paid:       { label: "Maksettu",              color: "text-green-300",    bg: "bg-green-500/15",   border: "border-green-500/30" },
  cancelled:  { label: "Peruutettu",            color: "text-red-300",      bg: "bg-red-500/15",     border: "border-red-500/30" },
};

const PAYMENT_METHODS = [
  { value: "cash",      label: "Käteinen",  icon: Banknote },
  { value: "card",      label: "Kortti",    icon: CreditCard },
  { value: "mobilepay", label: "MobilePay", icon: Smartphone },
  { value: "invoice",   label: "Lasku",     icon: FileText },
  { value: "other",     label: "Muu",       icon: FileText },
];

/* ════════════════════════════════════════════════════════════════════
 *  KOKKI (KITCHEN) – jonotetut tilaukset, status-muutos
 * ════════════════════════════════════════════════════════════════════ */
export function RestaurantKitchen() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/restaurant/orders/active");
      // Kokki näkee vain received + preparing + ready -tilaukset
      const filtered = (r.data || []).filter(o =>
        ["received", "preparing", "ready"].includes(o.status)
      );
      // Vanhin ensin (kokki tekee FIFO)
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setOrders(filtered);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [load]);

  const updateStatus = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await api.put(`/restaurant/orders/${orderId}/status`, { status: newStatus });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setUpdating(null);
  };

  const counts = {
    received: orders.filter(o => o.status === "received").length,
    preparing: orders.filter(o => o.status === "preparing").length,
    ready: orders.filter(o => o.status === "ready").length,
  };

  return (
    <div className="space-y-5 p-6 max-w-7xl mx-auto" data-testid="restaurant-kitchen">
      <Toaster richColors position="top-center" />
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">Keittiönäkymä</p>
          <h1 className="text-3xl font-display tracking-tight flex items-center gap-2">
            <ChefHat className="w-7 h-7 text-amber-300" /> Kokki
          </h1>
          <p className="text-xs text-white/40 mt-1">Tilaukset päivittyvät automaattisesti joka 5 sekuntia.</p>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30" data-testid="kpi-received">Vastaanotettu: {counts.received}</Badge>
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30" data-testid="kpi-preparing">Valmistuksessa: {counts.preparing}</Badge>
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30" data-testid="kpi-ready">Valmiit: {counts.ready}</Badge>
          <Button variant="outline" size="sm" onClick={() => navigate("/restaurant")} className="border-white/10">
            Palaa
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/40">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="glass-card p-12 text-center" data-testid="no-orders">
          <ChefHat className="w-12 h-12 mx-auto text-white/20 mb-3" />
          <p className="text-lg font-display text-white/60">Ei aktiivisia tilauksia</p>
          <p className="text-sm text-white/40 mt-1">Kun asiakas/tarjoilija tekee tilauksen, se näkyy täällä.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kitchen-orders">
          {orders.map((o) => {
            const st = STATUS_LABELS[o.status] || STATUS_LABELS.received;
            const minsAgo = Math.max(0, Math.floor((Date.now() - new Date(o.created_at)) / 60000));
            const nextStatus = o.status === "received" ? "preparing" : o.status === "preparing" ? "ready" : null;
            const nextLabel = o.status === "received" ? "Aloita valmistus" : o.status === "preparing" ? "Valmis tarjoiltavaksi" : "";
            return (
              <div
                key={o.id}
                className={`rounded-2xl border-2 p-4 space-y-3 ${st.bg} ${st.border} relative`}
                data-testid={`order-card-${o.id}`}
              >
                {/* Aikamerkkaus */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className={`text-[10px] uppercase tracking-widest ${st.color}`}>{st.label}</p>
                    <p className="text-xl font-display tracking-tight">{o.order_number}</p>
                    {o.table_number && (
                      <p className="text-xs text-white/60 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Pöytä {o.table_number}
                      </p>
                    )}
                  </div>
                  <Badge className={`${st.bg} ${st.color} border ${st.border} text-[10px]`}>
                    <Clock className="w-3 h-3 mr-1" /> {minsAgo}m
                  </Badge>
                </div>

                {/* Tilausrivit */}
                <ul className="space-y-1 text-sm border-t border-white/10 pt-2">
                  {(o.items || []).map((it, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="text-white font-medium">{it.quantity}× {it.name}</span>
                      {it.notes && <span className="text-amber-200 text-xs italic">{it.notes}</span>}
                    </li>
                  ))}
                </ul>

                {/* Asiakkaan kommentti */}
                {o.comment && (
                  <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-2 text-xs text-amber-200">
                    💬 {o.comment}
                  </div>
                )}

                {/* Action button */}
                {nextStatus && (
                  <Button
                    onClick={() => updateStatus(o.id, nextStatus)}
                    disabled={updating === o.id}
                    className="w-full btn-luxe inline-flex items-center justify-center gap-2 h-11"
                    data-testid={`advance-${o.id}`}
                  >
                    {updating === o.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : o.status === "received" ? (
                      <ChefHat className="w-4 h-4" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    {nextLabel} <ArrowRight className="w-4 h-4" />
                  </Button>
                )}

                {o.status === "ready" && (
                  <p className="text-center text-xs text-emerald-300 inline-flex items-center justify-center gap-1 w-full">
                    <CheckCircle2 className="w-3 h-3" /> Tarjoilijan tarjoiltavaksi
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 *  KASSA (CASHIER) – tarjoiltavat tilaukset + maksu
 * ════════════════════════════════════════════════════════════════════ */
export function RestaurantCashier() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingOrder, setPayingOrder] = useState(null);  // tilaus jolle avataan maksudialogi

  const load = useCallback(async () => {
    try {
      const r = await api.get("/restaurant/orders/active");
      // Kassa näkee ready + served (odottavat maksua)
      const filtered = (r.data || []).filter(o => ["ready", "served"].includes(o.status));
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setOrders(filtered);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, [load]);

  const todayTotal = useMemo(() => {
    // Approximate today's revenue from served+ready orders (live state)
    return orders.reduce((s, o) => s + Number(o.total || 0), 0);
  }, [orders]);

  return (
    <div className="space-y-5 p-6 max-w-6xl mx-auto" data-testid="restaurant-cashier">
      <Toaster richColors position="top-center" />
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">Kassanäkymä</p>
          <h1 className="text-3xl font-display tracking-tight flex items-center gap-2">
            <Receipt className="w-7 h-7 text-emerald-300" /> Kassa
          </h1>
          <p className="text-xs text-white/40 mt-1">Maksamattomat tilaukset – auto-refresh 8 s</p>
        </div>
        <div className="flex gap-2 items-center">
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-sm" data-testid="kpi-pending-total">
            Avoinna: {formatEur(todayTotal)}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => navigate("/restaurant")} className="border-white/10">Palaa</Button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/40">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="glass-card p-12 text-center" data-testid="no-pending">
          <Receipt className="w-12 h-12 mx-auto text-white/20 mb-3" />
          <p className="text-lg font-display text-white/60">Ei avoimia maksuja</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cashier-orders">
          {orders.map((o) => {
            const st = STATUS_LABELS[o.status];
            return (
              <div key={o.id} className={`rounded-2xl border-2 p-4 ${st.bg} ${st.border} space-y-3`} data-testid={`pay-card-${o.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`text-[10px] uppercase tracking-widest ${st.color}`}>{st.label}</p>
                    <p className="text-xl font-display">{o.order_number}</p>
                    {o.table_number && (
                      <p className="text-xs text-white/60 inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Pöytä {o.table_number}
                      </p>
                    )}
                  </div>
                  <span className="text-2xl font-mono text-emerald-300">{formatEur(o.total)}</span>
                </div>

                {/* Item summary */}
                <ul className="text-xs space-y-0.5 border-t border-white/10 pt-2">
                  {(o.items || []).map((it, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="text-white/80">{it.quantity}× {it.name}</span>
                      <span className="text-white/40 font-mono">{formatEur(it.price * it.quantity)}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => setPayingOrder(o)}
                  className="w-full btn-luxe inline-flex items-center justify-center gap-2 h-11"
                  data-testid={`pay-button-${o.id}`}
                >
                  <Banknote className="w-4 h-4" /> Vastaanota maksu
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <PaymentDialog
        order={payingOrder}
        onClose={() => setPayingOrder(null)}
        onPaid={() => { setPayingOrder(null); load(); }}
      />
    </div>
  );
}

function PaymentDialog({ order, onClose, onPaid }) {
  const [method, setMethod] = useState("card");
  const [tip, setTip] = useState(0);
  const [notes, setNotes] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => { if (order) { setMethod("card"); setTip(0); setNotes(""); } }, [order]);

  if (!order) return null;
  const total = Number(order.total || 0) + Number(tip || 0);

  const submit = async () => {
    setPaying(true);
    try {
      await api.put(`/restaurant/orders/${order.id}/status`, {
        status: "paid",
        payment_method: method,
        tip_amount: Number(tip) || 0,
        notes: notes.trim() || null,
      });
      toast.success(`Maksettu ${formatEur(total)}`);
      onPaid?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setPaying(false);
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="payment-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-300" />
            Vastaanota maksu · {order.order_number}
          </DialogTitle>
          <DialogDescription>
            Pöytä {order.table_number || "-"} · {formatEur(order.total)}
          </DialogDescription>
        </DialogHeader>

        {/* Maksutapa */}
        <div className="grid grid-cols-3 gap-2" data-testid="payment-methods">
          {PAYMENT_METHODS.slice(0, 3).map(pm => {
            const Icon = pm.icon;
            const active = method === pm.value;
            return (
              <button
                key={pm.value}
                onClick={() => setMethod(pm.value)}
                className={`p-3 rounded-xl border flex flex-col items-center gap-1 ${
                  active ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200" : "bg-white/[0.02] border-white/10 text-white/60"
                }`}
                data-testid={`pm-${pm.value}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs">{pm.label}</span>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2 -mt-1">
          {PAYMENT_METHODS.slice(3).map(pm => {
            const Icon = pm.icon;
            const active = method === pm.value;
            return (
              <button
                key={pm.value}
                onClick={() => setMethod(pm.value)}
                className={`p-3 rounded-xl border flex items-center gap-2 ${
                  active ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200" : "bg-white/[0.02] border-white/10 text-white/60"
                }`}
                data-testid={`pm-${pm.value}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs">{pm.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tipsi */}
        <div className="space-y-2">
          <p className="text-xs text-white/60">Tipsi (vapaaehtoinen)</p>
          <div className="grid grid-cols-5 gap-1" data-testid="tip-buttons">
            {[0, 5, 10, 15, 20].map(pct => (
              <button
                key={pct}
                onClick={() => setTip(Math.round((order.total * pct) / 100 * 100) / 100)}
                className={`py-2 rounded text-xs border ${
                  Math.abs(tip - (order.total * pct) / 100) < 0.01
                    ? "bg-amber-500/15 border-amber-400/40 text-amber-200"
                    : "border-white/10 text-white/60"
                }`}
                data-testid={`tip-${pct}`}
              >
                {pct === 0 ? "Ei" : `${pct}%`}
              </button>
            ))}
          </div>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={tip}
            onChange={(e) => setTip(parseFloat(e.target.value) || 0)}
            placeholder="Mukautettu tipsi €"
            data-testid="tip-custom"
          />
        </div>

        {/* Muistiinpanot */}
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Muistiinpano (vapaaehtoinen)"
          data-testid="payment-notes"
        />

        {/* Yhteensä */}
        <div className="flex justify-between items-center border-t border-white/10 pt-3">
          <span className="text-white/60 text-sm">Yhteensä</span>
          <span className="font-mono text-2xl text-emerald-300 font-semibold" data-testid="total-with-tip">
            {formatEur(total)}
          </span>
        </div>

        <Button
          onClick={submit}
          disabled={paying}
          className="w-full btn-luxe inline-flex items-center justify-center gap-2 h-12 text-base"
          data-testid="confirm-payment"
        >
          {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Vahvista maksu
        </Button>
      </DialogContent>
    </Dialog>
  );
}

/* ════════════════════════════════════════════════════════════════════
 *  TARJOILIJA (WAITER) – pöytäkaavio + tilaus pöydältä
 * ════════════════════════════════════════════════════════════════════ */
export function RestaurantWaiter() {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderingForTable, setOrderingForTable] = useState(null); // table object
  const [cart, setCart] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, o, c, p] = await Promise.all([
        api.get("/restaurant/tables"),
        api.get("/restaurant/orders/active"),
        api.get("/restaurant/categories"),
        api.get("/restaurant/products"),
      ]);
      setTables(t.data || []);
      setOrders(o.data || []);
      setCategories(c.data || []);
      setProducts(p.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, [load]);

  // Pöytien tila: aktiivinen tilaus?
  const tableStatus = useMemo(() => {
    const map = {};
    for (const t of tables) {
      const active = orders.filter(o => o.table_number === t.table_number);
      if (active.length === 0) {
        map[t.id] = { state: "free", orders: [] };
      } else if (active.some(o => o.status === "ready")) {
        map[t.id] = { state: "ready", orders: active };
      } else if (active.some(o => o.status === "preparing")) {
        map[t.id] = { state: "preparing", orders: active };
      } else if (active.some(o => o.status === "served")) {
        map[t.id] = { state: "served", orders: active };
      } else {
        map[t.id] = { state: "received", orders: active };
      }
    }
    return map;
  }, [tables, orders]);

  const tableColor = (state) => {
    switch (state) {
      case "free":      return "bg-white/[0.02] border-white/10 hover:border-emerald-400/30";
      case "received":  return "bg-blue-500/10 border-blue-500/30";
      case "preparing": return "bg-amber-500/10 border-amber-500/30";
      case "ready":     return "bg-emerald-500/10 border-emerald-500/30 animate-pulse";
      case "served":    return "bg-purple-500/10 border-purple-500/30";
      default:          return "bg-white/[0.02] border-white/10";
    }
  };
  const tableLabel = (state) => ({
    free: "Vapaa", received: "Tilaus otettu", preparing: "Kokissa",
    ready: "Valmis tarjoiltavaksi", served: "Tarjoiltu",
  })[state] || "—";

  // Tilausnäkymä
  const addItem = (p) => setCart(prev => {
    const ex = prev.findIndex(i => i.product_id === p.id);
    if (ex >= 0) {
      const copy = [...prev];
      copy[ex] = { ...copy[ex], quantity: copy[ex].quantity + 1 };
      return copy;
    }
    return [...prev, { product_id: p.id, name: p.name, price: p.price, vat_percent: p.vat_percent || 14, quantity: 1 }];
  });
  const updateQty = (i, d) => setCart(p => {
    const c = [...p]; c[i] = { ...c[i], quantity: Math.max(0, c[i].quantity + d) };
    return c.filter(x => x.quantity > 0);
  });
  const cartTotal = cart.reduce((s, it) => s + it.price * it.quantity, 0);
  const cartCount = cart.reduce((s, it) => s + it.quantity, 0);

  const submitOrder = async () => {
    if (cart.length === 0) return toast.error("Lisää tuotteita");
    setSubmitting(true);
    try {
      await api.post("/restaurant/orders", {
        table_number: orderingForTable.table_number,
        items: cart,
        comment: comment.trim() || null,
        source: "staff",
      });
      toast.success(`Tilaus lähetetty pöytään ${orderingForTable.table_number}`);
      setOrderingForTable(null); setCart([]); setComment("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  const markServed = async (orderId) => {
    try {
      await api.put(`/restaurant/orders/${orderId}/status`, { status: "served" });
      toast.success("Merkitty tarjoilluksi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5 p-6 max-w-7xl mx-auto" data-testid="restaurant-waiter">
      <Toaster richColors position="top-center" />
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">Tarjoilija</p>
          <h1 className="text-3xl font-display tracking-tight flex items-center gap-2">
            <Users className="w-7 h-7 text-cyan-300" /> Sali
          </h1>
          <p className="text-xs text-white/40 mt-1">{tables.length} pöytää · {orders.length} aktiivista tilausta</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/restaurant")} className="border-white/10">Palaa</Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/40">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="tables-grid">
          {tables.map(t => {
            const st = tableStatus[t.id] || { state: "free", orders: [] };
            const readyOrders = st.orders.filter(o => o.status === "ready");
            return (
              <button
                key={t.id}
                onClick={() => { setOrderingForTable(t); }}
                className={`rounded-2xl border-2 p-4 text-left transition ${tableColor(st.state)}`}
                data-testid={`table-${t.id}`}
              >
                <div className="flex items-start justify-between">
                  <span className="text-2xl font-display">P. {t.table_number}</span>
                  <span className="text-[10px] uppercase tracking-widest text-white/40">{t.location || ""}</span>
                </div>
                <p className="text-xs mt-1 text-white/70">{tableLabel(st.state)}</p>
                {st.orders.length > 0 && (
                  <p className="text-[10px] text-white/40 mt-0.5">{st.orders.length} tilausta · {formatEur(st.orders.reduce((s, o) => s + o.total, 0))}</p>
                )}
                {readyOrders.length > 0 && (
                  <div
                    role="button"
                    onClick={(e) => { e.stopPropagation(); markServed(readyOrders[0].id); }}
                    className="mt-2 w-full bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 rounded-lg py-1 text-[11px] text-emerald-200 inline-flex items-center justify-center gap-1 cursor-pointer"
                    data-testid={`mark-served-${t.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> Merkitse tarjoilluksi
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Tilauksen luonti pöydälle */}
      <Dialog open={!!orderingForTable} onOpenChange={(v) => !v && setOrderingForTable(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="waiter-order-dialog">
          <DialogHeader>
            <DialogTitle>Pöytä {orderingForTable?.table_number} – uusi tilaus</DialogTitle>
            <DialogDescription>Lisää tuotteet ja lähetä keittiöön.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Tuotelista */}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {categories.map(c => (
                <div key={c.id}>
                  <p className="text-[10px] uppercase tracking-widest text-amber-300/60 px-1">{c.name}</p>
                  <div className="grid grid-cols-1 gap-1.5">
                    {products.filter(p => p.category_id === c.id && p.is_available !== false).map(p => (
                      <button
                        key={p.id}
                        onClick={() => addItem(p)}
                        className="rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] px-3 py-2 text-left flex justify-between items-center"
                        data-testid={`waiter-prod-${p.id}`}
                      >
                        <span className="text-sm">{p.name}</span>
                        <span className="text-xs font-mono text-amber-300">{formatEur(p.price)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {/* Ostoskori */}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto bg-white/[0.02] rounded-lg p-3 border border-white/10">
              <p className="text-xs uppercase tracking-widest text-emerald-300/80 mb-2">
                <ShoppingBag className="w-3.5 h-3.5 inline mr-1" />
                Ostoskori ({cartCount})
              </p>
              {cart.length === 0 ? (
                <p className="text-xs text-white/30 italic text-center py-8">Tyhjä</p>
              ) : (
                <>
                  {cart.map((it, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm" data-testid={`waiter-cart-${i}`}>
                      <span className="flex-1 truncate">{it.name}</span>
                      <div className="flex items-center gap-1 bg-white/5 rounded px-1">
                        <Button size="sm" variant="ghost" onClick={() => updateQty(i, -1)} className="h-6 w-6 p-0"><Minus className="w-3 h-3" /></Button>
                        <span className="w-5 text-center text-xs font-mono">{it.quantity}</span>
                        <Button size="sm" variant="ghost" onClick={() => updateQty(i, 1)} className="h-6 w-6 p-0"><Plus className="w-3 h-3" /></Button>
                      </div>
                      <span className="text-xs font-mono text-amber-300 w-16 text-right">{formatEur(it.price * it.quantity)}</span>
                    </div>
                  ))}
                  <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Kommentti (esim. ei sipulia)" className="mt-2" data-testid="waiter-comment" />
                  <div className="flex justify-between border-t border-white/10 pt-2">
                    <span className="text-sm text-white/60">Yhteensä</span>
                    <span className="font-mono text-emerald-300 text-lg" data-testid="waiter-total">{formatEur(cartTotal)}</span>
                  </div>
                  <Button onClick={submitOrder} disabled={submitting} className="w-full btn-luxe" data-testid="waiter-submit">
                    {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    Lähetä keittiöön
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
